import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { t, font, Button, Input, Panel, Divider } from "@realtyflows/ui";
import { googleAuth, saveToken, getGoogleWithGmailUrl, sendVerificationCode } from "../api";
import Alert from "../components/Alert";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../config/firebase";
import PasswordInput from "../components/PasswordInput";
import useResponsive from "../hooks/useResponsive";
import useScrollToAlert from "../hooks/useScrollToError";

const passwordRules = [
  { test: p => p.length >= 8, label: "At least 8 characters" },
  { test: p => /[A-Z]/.test(p), label: "One uppercase letter" },
  { test: p => /[a-z]/.test(p), label: "One lowercase letter" },
  { test: p => /[0-9]/.test(p), label: "One number" },
  { test: p => /[!@#$%^&*()_+=[\]{};':"\\|,.<>/?]/.test(p), label: "One special character" },
];

function PasswordStrength({ password }) {
  if (!password) return null;
  const passed = passwordRules.filter(r => r.test(password)).length;
  const strength = passed <= 2 ? "Weak" : passed <= 4 ? "Medium" : "Strong";
  const color = passed <= 2 ? t.coral : passed <= 4 ? t.amber : "#22c55e";

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= passed ? color : t.border, transition: "background 0.2s" }} />
        ))}
      </div>
      <div style={{ fontSize: 10, color, fontWeight: 700, fontFamily: font.mono, marginBottom: 6 }}>{strength.toUpperCase()}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
        {passwordRules.map(r => {
          const ok = r.test(password);
          return (
            <div key={r.label} style={{ fontSize: 11, color: ok ? t.textPrimary : t.textMuted, fontFamily: font.body, display: "flex", alignItems: "center", gap: 5, fontWeight: ok ? 600 : 400 }}>
              <span style={{ fontSize: 12, color: ok ? "#22c55e" : t.border, fontWeight: 700 }}>{ok ? "✓" : "○"}</span> {r.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Signup({ onSignup, onPendingVerification }) {
  const navigate = useNavigate();
  const responsive = useResponsive();
  const isMobile = responsive.width < 640;
  const isCompact = responsive.isCompact;
  const [searchParams] = useSearchParams();
  const initialPlan = searchParams.get("plan") || "free";
  const [form, setForm] = useState({ name: "", email: "", phone: "", brokerage: "", password: "", plan: initialPlan });
  const [error, setError] = useState("");
  const errorRef = useScrollToAlert(error);
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [gmailPerms, setGmailPerms] = useState({ send: false });

  useEffect(() => {
    document.title = "Sign Up Free — RealtyFlows | Free Real Estate CRM";
    return () => { document.title = "RealtyFlows"; };
  }, []);

  const allRulesPass = passwordRules.every(r => r.test(form.password));
  const selectedGmailScopes = Object.entries(gmailPerms).filter(([, v]) => v).map(([k]) => k);

  const handleGoogleSignUp = async () => {
    setError("");
    setGoogleLoading(true);
    try {
      if (selectedGmailScopes.length > 0) {
        // Use backend OAuth flow with Gmail scopes
        const { url } = await getGoogleWithGmailUrl(selectedGmailScopes);
        window.location.href = url;
        return;
      }
      // No Gmail scopes — use normal Firebase popup
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();
      const { token, user } = await googleAuth(idToken, "signup");
      saveToken(token);
      onSignup(user);
    } catch (err) {
      if (err.code === "auth/popup-closed-by-user") return;
      setError(err.message || "Google sign-up failed");
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.name || !form.email || !form.password) {
      setError("Name, email, and password are required");
      return;
    }
    if (!allRulesPass) {
      setError("Password does not meet all requirements");
      return;
    }
    if (!agreed) {
      setError("You must agree to the Terms of Service and Privacy Policy");
      return;
    }

    setLoading(true);
    try {
      // Send 6-digit verification code via backend SMTP
      await sendVerificationCode({ email: form.email, purpose: "signup" });
      // Pass form data to parent — backend account is created after code verification
      onPendingVerification(form);
    } catch (err) {
      setError(err.message || "Failed to send verification code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: t.pageBg, padding: isMobile ? "24px 16px" : "40px 0" }}>
      <div className="rf-a1" style={{ width: isMobile ? "100%" : 460, maxWidth: 460 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: isMobile ? 20 : 32 }}>
          <div onClick={() => navigate("/")} style={{ display: "inline-flex", alignItems: "center", gap: isMobile ? 8 : 10, marginBottom: 8, cursor: "pointer" }}>
            <img src="/logo.png" alt="RealtyFlows" style={{ width: isMobile ? 24 : 28, height: isMobile ? 24 : 28, borderRadius: 4 }} />
            <span style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
              Realty<span style={{ color: t.gold }}>Flows</span>
            </span>
          </div>
          <div style={{ fontSize: isMobile ? 13 : 14, color: t.textSecondary, fontFamily: font.body }}>Create your account. Free forever. Pay when you sign.</div>
        </div>

        {/* Form */}
        <Panel padding={isMobile ? 16 : 32}>
          <Alert ref={errorRef} message={error} onDismiss={() => setError("")} style={{ marginBottom: 16 }} />

          <form onSubmit={handleSubmit}>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 12 : 14, marginBottom: 14 }}>
                  <Input label="Full Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="Alex Johnson" icon="◎" />
                  <Input label="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                    placeholder="(312) 555-0100" icon="✆" />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <Input label="Email *" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="alex@company.com" icon="✉" />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <Input label="Brokerage (optional)" value={form.brokerage} onChange={e => setForm({ ...form, brokerage: e.target.value })}
                    placeholder="Chicago Realty Group" />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <PasswordInput label="Password *" value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    placeholder="Min 8 chars, upper, lower, number, special" />
                  <PasswordStrength password={form.password} />
                </div>

                {/* Plan Selection */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: t.textSecondary, marginBottom: 8, fontFamily: font.body }}>Select Plan</label>
                  <div style={{ fontSize: 9, fontWeight: 700, color: t.textMuted, marginBottom: 4, fontFamily: font.mono, letterSpacing: 0.5 }}>SOLO AGENT</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 8 }}>
                    {[
                      { id: "free", name: "Free", price: "$0", sub: "1 signing" },
                      { id: "starter", name: "Starter", price: "$39/mo", sub: "3 signings" },
                      { id: "pro", name: "Pro", price: "$89/mo", sub: "10 signings", popular: true },
                    ].map(p => (
                      <div key={p.id} onClick={() => setForm({ ...form, plan: p.id })}
                        style={{
                          padding: isMobile ? "8px 4px" : "10px 6px", borderRadius: 10, textAlign: "center", cursor: "pointer",
                          background: form.plan === p.id ? t.emeraldLight : t.surfaceWarm,
                          border: form.plan === p.id ? `2px solid ${t.emerald}` : `1.5px solid ${t.border}`,
                          transition: "all 0.15s",
                        }}>
                        <div style={{ fontSize: isMobile ? 10 : 11, fontWeight: 700, color: form.plan === p.id ? t.emeraldDeep : t.textPrimary, fontFamily: font.body }}>{p.name}</div>
                        <div style={{ fontSize: isMobile ? 11 : 12, fontWeight: 800, color: t.textPrimary, marginTop: 2, fontFamily: font.mono }}>{p.price}</div>
                        <div style={{ fontSize: 9, color: p.popular ? t.emerald : t.textMuted, marginTop: 2, fontWeight: 600 }}>
                          {p.popular ? "RECOMMENDED" : p.sub}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: t.textMuted, marginBottom: 4, fontFamily: font.mono, letterSpacing: 0.5 }}>TEAM</div>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 8 }}>
                    {[
                      { id: "team_starter", name: "Team Starter", price: "$249/mo", sub: "5 users" },
                      { id: "team_pro", name: "Team Pro", price: "$549/mo", sub: "15 users" },
                      { id: "agency", name: "Agency", price: "$999/mo", sub: "50 users" },
                      { id: "enterprise", name: "Enterprise", price: "Contact Sales", sub: "Custom", contactSales: true },
                    ].map(p => (
                      <div key={p.id} onClick={() => p.contactSales ? navigate("/contact?plan=enterprise") : setForm({ ...form, plan: p.id })}
                        style={{
                          padding: isMobile ? "8px 4px" : "10px 6px", borderRadius: 10, textAlign: "center", cursor: "pointer",
                          background: form.plan === p.id ? t.emeraldLight : t.surfaceWarm,
                          border: form.plan === p.id ? `2px solid ${t.emerald}` : `1.5px solid ${t.border}`,
                          transition: "all 0.15s",
                        }}>
                        <div style={{ fontSize: isMobile ? 9 : 10, fontWeight: 700, color: form.plan === p.id ? t.emeraldDeep : t.textPrimary, fontFamily: font.body }}>{p.name}</div>
                        <div style={{ fontSize: isMobile ? 10 : 11, fontWeight: 800, color: t.textPrimary, marginTop: 2, fontFamily: font.mono }}>{p.price}</div>
                        <div style={{ fontSize: 9, color: t.textMuted, marginTop: 2, fontWeight: 600 }}>
                          {p.contactSales ? "CONTACT SALES" : p.sub}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Terms Agreement */}
                <div style={{ marginBottom: isMobile ? 16 : 20, display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                    style={{ marginTop: 2, accentColor: t.emerald, width: 16, height: 16, cursor: "pointer", flexShrink: 0 }} />
                  <label style={{ fontSize: isMobile ? 11 : 12, color: t.textSecondary, lineHeight: 1.5, fontFamily: font.body, cursor: "pointer" }} onClick={() => setAgreed(!agreed)}>
                    I agree to the{" "}
                    <span onClick={e => { e.stopPropagation(); navigate("/terms"); }} style={{ color: t.emerald, fontWeight: 600, textDecoration: "underline", cursor: "pointer" }}>Terms of Service</span>
                    {" "}and{" "}
                    <span onClick={e => { e.stopPropagation(); navigate("/privacy"); }} style={{ color: t.emerald, fontWeight: 600, textDecoration: "underline", cursor: "pointer" }}>Privacy Policy</span>
                  </label>
                </div>

                <Button variant="primary" size="lg" fullWidth type="submit" disabled={loading || !agreed || !allRulesPass}>
                  {loading ? "Creating account..." : "Create Account"}
                </Button>
              </form>

              <div style={{ fontSize: isMobile ? 10 : 11, color: t.textMuted, textAlign: "center", marginTop: 14, lineHeight: 1.5, fontFamily: font.body }}>
                Free CRM forever. 1 free signing included.
              </div>

              <Divider label="or" />

              {/* Gmail Permission (optional) */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, marginBottom: 8, fontFamily: font.body }}>
                  Gmail Access <span style={{ fontWeight: 400, color: t.textMuted }}>(optional)</span>
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, color: t.textSecondary, fontFamily: font.body }}>
                  <input type="checkbox" checked={gmailPerms.send}
                    onChange={e => setGmailPerms({ send: e.target.checked })}
                    style={{ accentColor: t.emerald, width: 14, height: 14, cursor: "pointer" }} />
                  Send emails
                </label>
                {selectedGmailScopes.length > 0 && (
                  <div style={{ fontSize: 10, color: t.textMuted, marginTop: 6, fontFamily: font.body }}>
                    Gmail send permission will be requested during Google sign-up
                  </div>
                )}
              </div>

              <button onClick={handleGoogleSignUp} disabled={googleLoading}
                style={{
                  width: "100%", padding: isMobile ? "10px 14px" : "12px 16px", borderRadius: 10, cursor: googleLoading ? "not-allowed" : "pointer",
                  background: t.surfaceWarm, border: `1.5px solid ${t.border}`, display: "flex", alignItems: "center",
                  justifyContent: "center", gap: 10, fontFamily: font.body, fontSize: isMobile ? 13 : 14, fontWeight: 600,
                  color: t.textPrimary, transition: "all 0.15s", opacity: googleLoading ? 0.7 : 1,
                }}
                onMouseEnter={e => { if (!googleLoading) e.currentTarget.style.borderColor = t.emerald; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; }}
              >
                <svg width="18" height="18" viewBox="0 0 48 48">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
                {googleLoading ? "Signing up..." : "Sign up with Google"}
              </button>
              <div style={{ fontSize: 10, color: t.textMuted, textAlign: "center", marginTop: 8, fontFamily: font.body, lineHeight: 1.5 }}>
                By signing up with Google, you agree to our{" "}
                <span onClick={() => navigate("/terms")} style={{ color: t.emerald, cursor: "pointer", textDecoration: "underline" }}>Terms</span>
                {" "}and{" "}
                <span onClick={() => navigate("/privacy")} style={{ color: t.emerald, cursor: "pointer", textDecoration: "underline" }}>Privacy Policy</span>
              </div>
        </Panel>

        <div style={{ textAlign: "center", marginTop: isMobile ? 16 : 20, fontSize: isMobile ? 12 : 13, color: t.textSecondary, fontFamily: font.body }}>
          Already have an account?{" "}
          <span onClick={() => navigate("/login")} style={{ color: t.emerald, fontWeight: 600, cursor: "pointer" }}>Sign in</span>
        </div>
        <div style={{ textAlign: "center", marginTop: 10 }}>
          <span onClick={() => navigate("/")} style={{ fontSize: 12, color: t.textMuted, cursor: "pointer", fontFamily: font.body }}>&larr; Back to home</span>
        </div>
      </div>
    </div>
  );
}
